<script>
  import { matchSetup, availableTeams, currentScreen, savedMatches, currentUser } from '../store.js';
  import { setMeta } from '../../store.js';
  import { toast } from '../../ui/dialogs.js';
  import { searchGlobalTeam } from '../../services/cloud-sync.js';
  import Icon from '../components/Icon.svelte';

  let showCustomRules = false;

  // ── Cloud team import (@handle → squad with ADD/REMOVE toggles) ──
  const DEFAULT_TEAM_LOGO = 'data:image/svg+xml;base64,' + btoa(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1e3a5f"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>'
  );
  let showImportModal = false;
  let importSlot = 'teamA';
  let importHandle = '';
  let importedTeam = null;   // team doc from cloud
  let importSelection = [];  // [{...rosterEntry, selected: bool}]
  let isImporting = false;

  function openTeamImport(slot) {
    if (!$currentUser?.uid) { toast('Login required to import cloud teams.', 'error'); return; }
    importSlot = slot;
    importHandle = '';
    importedTeam = null;
    importSelection = [];
    showImportModal = true;
  }

  async function fetchTeamByHandle() {
    const handle = importHandle.trim();
    if (!handle) return;
    isImporting = true;
    importedTeam = null;
    try {
      const team = await searchGlobalTeam(handle);
      if (!team) {
        toast(`Team @${handle.replace(/^@/, '')} not found.`, 'error');
      } else if (!team.roster || team.roster.length === 0) {
        toast('This team has an empty roster.', 'error');
      } else {
        importedTeam = team;
        // Preselect the last-played XI if the team has one; otherwise everyone.
        const lastXI = Array.isArray(team.lastXI) && team.lastXI.length > 0 ? team.lastXI : null;
        importSelection = team.roster.map(p => ({
          ...p,
          selected: lastXI ? lastXI.includes(p.name) : true
        }));
        if (lastXI && !importSelection.some(p => p.selected)) {
          importSelection = importSelection.map(p => ({ ...p, selected: true }));
        }
      }
    } catch (err) {
      toast('Team search failed: ' + (err?.message || err), 'error');
    }
    isImporting = false;
  }

  function confirmTeamImport() {
    const picked = importSelection.filter(p => p.selected);
    if (picked.length < 1) { toast('Select at least one player.', 'error'); return; }
    const otherSlot = importSlot === 'teamA' ? 'teamB' : 'teamA';
    if ($matchSetup[otherSlot]?.name === importedTeam.name) {
      toast('Both teams cannot be the same.', 'error'); return;
    }

    // Link registered players locally so career stats sync to their accounts.
    try {
      const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
      picked.forEach(p => {
        if (p.uid) reg[p.name] = { uid: p.uid, displayName: p.name, username: p.username || '', photo: p.photo || '' };
      });
      localStorage.setItem('cs_player_registry', JSON.stringify(reg));
    } catch (_) {}

    const names = picked.map(p => p.name);
    $matchSetup[importSlot] = {
      name: importedTeam.name,
      captain: names[0],
      color: importSlot === 'teamA' ? '#2563eb' : '#dc2626',
      logo: DEFAULT_TEAM_LOGO,
      players: names,
      cloudTeamId: importedTeam.id,
      cloudUsername: importedTeam.username
    };
    $matchSetup[importSlot === 'teamA' ? 'playersA' : 'playersB'] = [...names];
    $matchSetup = $matchSetup;
    showImportModal = false;
    toast(`@${importedTeam.username} imported — ${names.length} players ready!`, 'success');
  }

  // Head-to-head record between the two selected teams, from saved history.
  $: h2h = (() => {
    const a = $matchSetup?.teamA?.name, b = $matchSetup?.teamB?.name;
    if (!a || !b || a === b || !$savedMatches?.length) return null;
    let winsA = 0, winsB = 0, ties = 0, played = 0, lastWinner = null;
    for (const m of $savedMatches) {
      const names = [m.teamA?.name, m.teamB?.name];
      if (!(names.includes(a) && names.includes(b))) continue;
      if (!m.result || m.result.winner === 'COMPLETED') continue;
      played++;
      if (m.result.winner === 'TIE') { ties++; continue; }
      const winnerName = m.result.winner === 'A' ? m.teamA.name : m.teamB.name;
      if (winnerName === a) { winsA++; lastWinner = a; }
      else { winsB++; lastWinner = b; }
    }
    return played > 0 ? { winsA, winsB, ties, played, lastWinner } : null;
  })();

  if (!$matchSetup) {
    matchSetup.set({
      teamA: null, teamB: null,
      overs: 10, mode: "full", lms: true,
      tossWinner: "A", tossDecision: "bat",
      rules: {
        maxOversPerBowler: 2,
        wideValue: 1,
        noBallValue: 1,
        rebowlWides: true,
        rebowlNoBalls: true
      }
    });
  }

  $: if ($matchSetup && $matchSetup.overs) {
    if (!$matchSetup.rules) {
      $matchSetup.rules = {
        maxOversPerBowler: Math.ceil($matchSetup.overs / 5),
        wideValue: 1,
        noBallValue: 1,
        rebowlWides: true,
        rebowlNoBalls: true
      };
      $matchSetup = $matchSetup;
    } else if ($matchSetup.rules.maxOversPerBowler > $matchSetup.overs) {
      $matchSetup.rules.maxOversPerBowler = $matchSetup.overs;
      $matchSetup = $matchSetup;
    }
  }

  function selectTeam(slot, team) {
    $matchSetup[slot] = team; $matchSetup = $matchSetup;
  }

  function clearTeam(slot) {
    $matchSetup[slot] = null; $matchSetup = $matchSetup;
  }

  function goHome() {
    currentScreen.set('home');
  }

  function next() {
    if (!$matchSetup.teamA || !$matchSetup.teamB) {
      toast("Pick both teams", "error"); return;
    }
    if ($matchSetup.teamA.name === $matchSetup.teamB.name) {
      toast("Both teams cannot be the same", "error"); return;
    }
    
    // Save metadata
    setMeta($matchSetup.teamA.name, { color: $matchSetup.teamA.color, logo: $matchSetup.teamA.logo });
    setMeta($matchSetup.teamB.name, { color: $matchSetup.teamB.color, logo: $matchSetup.teamB.logo });
    
    currentScreen.set('players');
  }

  const oversOptions = [5, 6, 8, 10, 15, 20, 50];
</script>

<div class="screen">
  <button class="btn btn-ghost" on:click={goHome}>← Back</button>
  <h1 style="margin-top:10px">Match Setup</h1>

  <!-- TEAMS SELECTION -->
  <div class="card">
    <div style="font-weight:800; margin-bottom:12px; letter-spacing:1px; color:#fbbf24; font-size:13px; text-transform:uppercase; display:flex; align-items:center; gap:6px;">
      <Icon name="shield" size={16} /> Teams Selection
    </div>
    
    <div class="field" data-testid="team-a-field">
      <div id="team-a-label" style="font-size:11px; text-transform:uppercase; color:#9ca3af; font-weight:800; margin-bottom:8px; display:block;">Team A</div>
      {#if $matchSetup.teamA}
        <div class="team-tile team-selected" data-testid="team-a-selected" aria-labelledby="team-a-label" style="border-color: {$matchSetup.teamA.color}; box-shadow: 0 0 10px {$matchSetup.teamA.color}40; display:flex; align-items:center; justify-content:space-between; background: rgba(15, 23, 42, 0.8); border-radius: 8px; padding: 12px; border-width: 1px; border-style: solid;">
          <div style="display:flex; align-items:center; gap:12px;">
            <div style="width:14px; height:14px; border-radius:50%; background:{$matchSetup.teamA.color}; box-shadow: 0 0 8px {$matchSetup.teamA.color};"></div>
            <div>
              <div style="font-weight:900; font-size:15px; color:#fff;">{$matchSetup.teamA.name}</div>
              <div style="font-size:11px; color:#94a3b8; font-weight:700;">c: {$matchSetup.teamA.captain}</div>
            </div>
          </div>
          <button class="btn btn-ghost" style="padding:6px 10px; font-size:11px; margin:0; color:#ef4444; border-color:#7f1d1d;" on:click={() => clearTeam('teamA')}>
            ✕ Change
          </button>
        </div>
      {:else}
        <div class="team-grid" role="group" aria-labelledby="team-a-label" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 8px;">
          {#each $availableTeams as t, i_each (t.id || t.name || i_each)}
            <button 
              class="team-tile" 
              disabled={$matchSetup.teamB?.name === t.name}
              style="text-align:left; background: rgba(15, 23, 42, 0.6); border: 1px solid #334155; border-radius: 8px; padding: 10px; cursor:pointer; transition: all 0.2s; opacity: {$matchSetup.teamB && $matchSetup.teamB.name === t.name ? '0.3' : '1'}; pointer-events: {$matchSetup.teamB && $matchSetup.teamB.name === t.name ? 'none' : 'auto'}; backdrop-filter: blur(4px);"
              on:click={() => selectTeam('teamA', t)}
            >
              <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;">
                <div style="width:10px; height:10px; border-radius:50%; background:{t.color};"></div>
                <div style="font-weight:800; font-size:13px; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">{t.name}</div>
              </div>
              <div style="font-size:10px; color:#94a3b8; font-weight:700;">c: {t.captain}</div>
            </button>
          {/each}
        </div>
      {/if}
      <button class="btn btn-ghost" style="margin-top:8px; font-size:11px; padding:6px 10px; color:#34d399; border-color:#065f46; display:flex; align-items:center; justify-content:center; gap:6px; width:100%;" on:click={() => openTeamImport('teamA')}>
        <Icon name="globe" size={14} /> Import Cloud Team (@handle)
      </button>
      {#if $matchSetup.teamA?.cloudUsername}
        <div class="muted" style="font-size:10px; margin-top:4px; color:#34d399; text-align:center;">✓ Cloud team @{$matchSetup.teamA.cloudUsername} — {$matchSetup.teamA.players?.length || 0} players imported</div>
      {/if}
    </div>

    <div style="display:flex; justify-content:center; align-items:center; margin: 16px 0;">
      <div style="height:1px; background:#334155; flex:1;"></div>
      <div style="padding:0 12px; font-weight:900; font-style:italic; color:#64748b; font-size:14px; letter-spacing:1px;">VS</div>
      <div style="height:1px; background:#334155; flex:1;"></div>
    </div>

    <div class="field" data-testid="team-b-field">
      <div id="team-b-label" style="font-size:11px; text-transform:uppercase; color:#9ca3af; font-weight:800; margin-bottom:8px; display:block;">Team B</div>
      {#if $matchSetup.teamB}
        <div class="team-tile team-selected" data-testid="team-b-selected" aria-labelledby="team-b-label" style="border-color: {$matchSetup.teamB.color}; box-shadow: 0 0 10px {$matchSetup.teamB.color}40; display:flex; align-items:center; justify-content:space-between; background: rgba(15, 23, 42, 0.8); border-radius: 8px; padding: 12px; border-width: 1px; border-style: solid;">
          <div style="display:flex; align-items:center; gap:12px;">
            <div style="width:14px; height:14px; border-radius:50%; background:{$matchSetup.teamB.color}; box-shadow: 0 0 8px {$matchSetup.teamB.color};"></div>
            <div>
              <div style="font-weight:900; font-size:15px; color:#fff;">{$matchSetup.teamB.name}</div>
              <div style="font-size:11px; color:#94a3b8; font-weight:700;">c: {$matchSetup.teamB.captain}</div>
            </div>
          </div>
          <button class="btn btn-ghost" style="padding:6px 10px; font-size:11px; margin:0; color:#ef4444; border-color:#7f1d1d;" on:click={() => clearTeam('teamB')}>
            ✕ Change
          </button>
        </div>
      {:else}
        <div class="team-grid" role="group" aria-labelledby="team-b-label" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 8px;">
          {#each $availableTeams as t, i_each (t.id || t.name || i_each)}
            <button 
              class="team-tile" 
              disabled={$matchSetup.teamA?.name === t.name}
              style="text-align:left; background: rgba(15, 23, 42, 0.6); border: 1px solid #334155; border-radius: 8px; padding: 10px; cursor:pointer; transition: all 0.2s; opacity: {$matchSetup.teamA && $matchSetup.teamA.name === t.name ? '0.3' : '1'}; pointer-events: {$matchSetup.teamA && $matchSetup.teamA.name === t.name ? 'none' : 'auto'}; backdrop-filter: blur(4px);"
              on:click={() => selectTeam('teamB', t)}
            >
              <div style="display:flex; align-items:center; gap:6px; margin-bottom:4px;">
                <div style="width:10px; height:10px; border-radius:50%; background:{t.color};"></div>
                <div style="font-weight:800; font-size:13px; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">{t.name}</div>
              </div>
              <div style="font-size:10px; color:#94a3b8; font-weight:700;">c: {t.captain}</div>
            </button>
          {/each}
        </div>
      {/if}
      <button class="btn btn-ghost" style="margin-top:8px; font-size:11px; padding:6px 10px; color:#34d399; border-color:#065f46; display:flex; align-items:center; justify-content:center; gap:6px; width:100%;" on:click={() => openTeamImport('teamB')}>
        <Icon name="globe" size={14} /> Import Cloud Team (@handle)
      </button>
      {#if $matchSetup.teamB?.cloudUsername}
        <div class="muted" style="font-size:10px; margin-top:4px; color:#34d399; text-align:center;">✓ Cloud team @{$matchSetup.teamB.cloudUsername} — {$matchSetup.teamB.players?.length || 0} players imported</div>
      {/if}
    </div>
  </div>

  <!-- HEAD TO HEAD (from saved match history) -->
  {#if h2h}
    <div class="card" style="padding:12px 16px; border-color:#7c3aed; background:rgba(124,58,237,0.06);">
      <div style="font-size:10px; font-weight:900; letter-spacing:1.2px; color:#c084fc; text-transform:uppercase; margin-bottom:6px; display:flex; align-items:center; gap:4px;">
        <Icon name="zap" size={14} /> Head to Head — {h2h.played} match{h2h.played > 1 ? 'es' : ''}
      </div>
      <div style="display:flex; justify-content:space-between; align-items:center; font-weight:900; font-size:14px;">
        <span style="color:#fff;">{$matchSetup.teamA.name} <span style="color:#34d399;">{h2h.winsA}</span></span>
        <span style="color:#64748b; font-size:11px;">{h2h.ties > 0 ? h2h.ties + ' tie' + (h2h.ties > 1 ? 's' : '') : '—'}</span>
        <span style="color:#fff;"><span style="color:#34d399;">{h2h.winsB}</span> {$matchSetup.teamB.name}</span>
      </div>
      {#if h2h.lastWinner}
        <div class="muted" style="font-size:10px; margin-top:4px;">Last win: {h2h.lastWinner}</div>
      {/if}
    </div>
  {/if}

  <!-- MATCH SETTINGS (LMS & OVERS) -->
  <div class="card">
    <div class="field">
      <div id="lms-label" class="field-label">Last Man Stands (LMS)</div>
      <div class="toggle-group" role="group" aria-labelledby="lms-label">
        <button class="btn {$matchSetup.lms ? 'active' : ''}" on:click={() => $matchSetup.lms = true}>ON</button>
        <button class="btn {!$matchSetup.lms ? 'active' : ''}" on:click={() => $matchSetup.lms = false}>OFF</button>
      </div>
    </div>
    <div class="field" style="margin-top: 15px;">
      <label for="match-overs">Overs limit</label>
      <select id="match-overs" bind:value={$matchSetup.overs}>
        {#each oversOptions as n, i_each (n.id || n.name || i_each)}
          <option value={n}>{n} overs</option>
        {/each}
      </select>
    </div>
  </div>

  <!-- CUSTOM MATCH RULES -->
  {#if $matchSetup.rules}
    <div class="card" style="margin-top: 16px;">
      <button class="btn btn-ghost btn-block" style="text-align: left; display: flex; justify-content: space-between; align-items: center; font-weight: 800; padding: 12px 16px;" on:click={() => showCustomRules = !showCustomRules}>
        <span style="display:flex; align-items:center; gap:6px;"><Icon name="settings" size={16} /> CUSTOM MATCH RULES</span>
        <span style="font-size: 12px;">{showCustomRules ? '▲ Close' : '▼ Customize'}</span>
      </button>

      {#if showCustomRules}
        <div style="margin-top: 15px; border-top: 1px dashed #334155; padding-top: 15px;">
          <div class="field">
            <label for="max-bowler-overs">Max Overs Per Bowler</label>
            <input type="number" id="max-bowler-overs" bind:value={$matchSetup.rules.maxOversPerBowler} min="1" max={$matchSetup.overs} style="width: 100%; padding: 10px; background: #0f172a; border: 1px solid #334155; color: white; border-radius: 6px; font-size: 16px;">
          </div>

          <div class="field" style="margin-top: 12px;">
            <label for="custom-wide-value">Wide Ball Runs</label>
            <select id="custom-wide-value" bind:value={$matchSetup.rules.wideValue} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;">
              <option value={0}>0 Runs (No Penalty)</option>
              <option value={1}>1 Run</option>
              <option value={2}>2 Runs</option>
            </select>
          </div>

          <div class="field" style="margin-top: 12px;">
            <label for="custom-nb-value">No Ball Runs</label>
            <select id="custom-nb-value" bind:value={$matchSetup.rules.noBallValue} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;">
              <option value={0}>0 Runs (No Penalty)</option>
              <option value={1}>1 Run</option>
              <option value={2}>2 Runs</option>
            </select>
          </div>

          <div class="field" style="margin-top: 12px;">
            <div id="rebowl-wide-label" class="field-label">Rebowl Wides</div>
            <div class="toggle-group" role="group" aria-labelledby="rebowl-wide-label">
              <button class="btn {$matchSetup.rules.rebowlWides ? 'active' : ''}" on:click={() => $matchSetup.rules.rebowlWides = true}>YES</button>
              <button class="btn {!$matchSetup.rules.rebowlWides ? 'active' : ''}" on:click={() => $matchSetup.rules.rebowlWides = false}>NO</button>
            </div>
          </div>

          <div class="field" style="margin-top: 12px;">
            <div id="rebowl-nb-label" class="field-label">Rebowl No-Balls</div>
            <div class="toggle-group" role="group" aria-labelledby="rebowl-nb-label">
              <button class="btn {$matchSetup.rules.rebowlNoBalls ? 'active' : ''}" on:click={() => $matchSetup.rules.rebowlNoBalls = true}>YES</button>
              <button class="btn {!$matchSetup.rules.rebowlNoBalls ? 'active' : ''}" on:click={() => $matchSetup.rules.rebowlNoBalls = false}>NO</button>
            </div>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  <!-- TOSS -->
  {#if $matchSetup.teamA && $matchSetup.teamB}
    <div class="card">
      <div class="field">
        <div id="toss-winner-label" class="field-label">Toss won by</div>
        <div class="toggle-group" role="group" aria-labelledby="toss-winner-label">
          <button class="btn {$matchSetup.tossWinner === 'A' ? 'active' : ''}" on:click={() => $matchSetup.tossWinner = 'A'}>
            {$matchSetup.teamA.name}
          </button>
          <button class="btn {$matchSetup.tossWinner === 'B' ? 'active' : ''}" on:click={() => $matchSetup.tossWinner = 'B'}>
            {$matchSetup.teamB.name}
          </button>
        </div>
      </div>
      <div class="field">
        <div id="toss-decision-label" class="field-label">Decided to</div>
        <div class="toggle-group" role="group" aria-labelledby="toss-decision-label">
          <button class="btn {$matchSetup.tossDecision === 'bat' ? 'active' : ''}" on:click={() => $matchSetup.tossDecision = 'bat'}>Bat</button>
          <button class="btn {$matchSetup.tossDecision === 'bowl' ? 'active' : ''}" on:click={() => $matchSetup.tossDecision = 'bowl'}>Bowl</button>
        </div>
      </div>
    </div>
  {/if}

  <button class="btn btn-primary btn-block"
          disabled={!$matchSetup.teamA || !$matchSetup.teamB}
          on:click={next}>
    NEXT: PLAYERS →
  </button>
</div>

{#if showImportModal}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-backdrop" on:click={() => showImportModal = false}>
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="modal-content" on:click|stopPropagation>
      <h3 style="color:#34d399; margin:0 0 6px 0; font-weight:900; display:flex; align-items:center; gap:8px;">
        <Icon name="globe" size={20} /> Import Cloud Team — {importSlot === 'teamA' ? 'Team A' : 'Team B'}
      </h3>
      <p style="font-size:12px; color:#94a3b8; margin:0 0 14px 0; font-weight:600;">Opponent team ka @handle daalo (unke captain se poocho).</p>

      <div style="display:flex; gap:8px;">
        <input type="text" bind:value={importHandle} placeholder="@team_handle" style="flex:1; padding:12px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:800;" on:keydown={(e) => e.key === 'Enter' && fetchTeamByHandle()} />
        <button class="btn btn-primary" style="margin:0;" on:click={fetchTeamByHandle} disabled={isImporting}>{isImporting ? '...' : 'Find'}</button>
      </div>

      {#if importedTeam}
        <div style="margin-top:14px; padding:10px 12px; background:#0f172a; border-radius:8px; border:1px solid #065f46;">
          <div style="font-weight:900; color:#fff;">{importedTeam.name}</div>
          <div style="font-size:11px; color:#94a3b8;">@{importedTeam.username} · {importedTeam.roster.length} players in squad</div>
        </div>

        <div style="margin-top:10px; font-size:11px; color:#94a3b8; font-weight:700; text-transform:uppercase; letter-spacing:0.5px;">
          Select today's playing squad
          {#if importedTeam.lastXI?.length}<span style="color:#34d399;"> (last played XI preselected)</span>{/if}
        </div>

        {#each importSelection as p, i (p.name)}
          <div style="display:flex; align-items:center; gap:10px; padding:7px 0; border-bottom:1px solid #0f172a;">
            {#if p.photo}
              <img src={p.photo} alt="avatar" style="width:28px; height:28px; border-radius:50%; object-fit:cover;" />
            {:else}
              <div style="width:28px; height:28px; border-radius:50%; background:#334155; display:flex; align-items:center; justify-content:center; color:#94a3b8;">
                <Icon name="user" size={14} />
              </div>
            {/if}
            <div style="flex:1; min-width:0;">
              <div style="font-size:13px; font-weight:700; color:{p.selected ? '#fff' : '#64748b'}; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{p.name}</div>
              <div style="font-size:10px; color:#64748b;">{p.username ? '@' + p.username : p.uid ? 'linked' : 'guest'}</div>
            </div>
            <button class="btn {p.selected ? 'btn-primary' : 'btn-ghost'}" style="padding:4px 12px; font-size:12px; font-weight:900; margin:0; {p.selected ? 'background:#059669; border:none;' : 'color:#94a3b8;'}" on:click={() => { importSelection[i].selected = !importSelection[i].selected; }}>
              {p.selected ? '✓ IN' : '+ ADD'}
            </button>
          </div>
        {/each}

        <button class="btn btn-primary btn-block" style="margin-top:16px; background:linear-gradient(135deg,#059669,#10b981); border:none; font-weight:900;" on:click={confirmTeamImport}>
          IMPORT {importSelection.filter(p => p.selected).length} PLAYERS ▶
        </button>
      {/if}

      <button class="btn btn-ghost btn-block" style="margin-top:10px;" on:click={() => showImportModal = false}>Cancel</button>
    </div>
  </div>
{/if}
